import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

const Feedback = () => {
  const [feedback, setFeedback] = useState('');
  const [hasExistingOverallFeedback, setHasExistingOverallFeedback] = useState(false);
  const [isEditingOverallFeedback, setIsEditingOverallFeedback] = useState(false);
  const [overallLoading, setOverallLoading] = useState(true);
  const [schoolRating, setSchoolRating] = useState(0);
  const [schoolReview, setSchoolReview] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [hasExistingSchoolRating, setHasExistingSchoolRating] = useState(false);
  const [isEditingSchoolRating, setIsEditingSchoolRating] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(true);
  const [ratingSaving, setRatingSaving] = useState(false);
  const [ratingError, setRatingError] = useState(null);
  const [ratingSuccess, setRatingSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { bearerAuth } = useApi();
  const { user, viewMode } = useAuth();

  useEffect(() => {
    let isMounted = true;

    const loadSchoolRating = async () => {
      try {
        setRatingLoading(true);
        setRatingError(null);
        const response = await bearerAuth('school-rating', 'GET');
        const data = response?.data?.data;
        if (!isMounted || !response?.data?.status || !data) return;
        setSchoolName(data.school_name || '');
        setSchoolRating(Number(data.rating) || 0);
        setSchoolReview(data.review || '');
        const alreadyRated = Boolean(Number(data.rating));
        setHasExistingSchoolRating(alreadyRated);
        setIsEditingSchoolRating(!alreadyRated);
      } catch (err) {
        if (!isMounted) return;
        const status = err?.response?.status;
        if (status === 404) {
          setSchoolName('');
          setSchoolRating(0);
          setSchoolReview('');
          setHasExistingSchoolRating(false);
          setIsEditingSchoolRating(false);
        } else {
          setRatingError(err?.response?.data?.message || err.message || 'Failed to load driving school rating.');
        }
      } finally {
        if (isMounted) {
          setRatingLoading(false);
        }
      }
    };

    loadSchoolRating();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadOverallFeedback = async () => {
      try {
        setOverallLoading(true);
        setError(null);
        const response = await bearerAuth('feedback/me', 'GET', {
          user_type: viewMode || 'user',
        });
        const data = response?.data?.data;
        if (!isMounted) return;

        if (data?.feedback) {
          setFeedback(data.feedback);
          setHasExistingOverallFeedback(true);
          setIsEditingOverallFeedback(false);
        } else {
          setFeedback('');
          setHasExistingOverallFeedback(false);
          setIsEditingOverallFeedback(true);
        }
      } catch (err) {
        if (!isMounted) return;
        setFeedback('');
        setHasExistingOverallFeedback(false);
        setIsEditingOverallFeedback(true);
      } finally {
        if (isMounted) {
          setOverallLoading(false);
        }
      }
    };

    loadOverallFeedback();

    return () => {
      isMounted = false;
    };
  }, [viewMode]);

  const handleSchoolRatingSubmit = async (e) => {
    e.preventDefault();

    if (!schoolRating) {
      setRatingError('Please select a driving school rating before submitting.');
      return;
    }

    setRatingSaving(true);
    setRatingError(null);
    setRatingSuccess(false);

    try {
      const response = await bearerAuth('school-rating', 'POST', {
        rating: schoolRating,
        review: schoolReview.trim(),
      });

      if (response?.data?.status) {
        setHasExistingSchoolRating(true);
        setIsEditingSchoolRating(false);
        setRatingSuccess(true);
        setTimeout(() => setRatingSuccess(false), 5000);
      } else {
        throw new Error(response?.data?.message || 'Failed to submit school rating');
      }
    } catch (err) {
      setRatingError(err?.response?.data?.message || err.message || 'Failed to submit school rating.');
    } finally {
      setRatingSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!feedback.trim()) {
      setError('Please enter your feedback before submitting.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const feedbackData = {
        user_type: viewMode || 'user',
        feedback: feedback.trim(),
        status: 1
      };

      const response = await bearerAuth('feedback', 'POST', feedbackData);

      // UPDATED SUCCESS CONDITION
      if (response.data && (response.data.success || response.data.message)) {
        setHasExistingOverallFeedback(true);
        setIsEditingOverallFeedback(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);
      } else {
        throw new Error(response.data?.message || 'Failed to submit feedback');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(
        err.message || 'An error occurred while submitting feedback. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackChange = (e) => {
    setFeedback(e.target.value);
    if (error) setError(null);
  };

  return (
    <div className="feedback-container">
      <h2 className="mb-4">SHARE YOUR FEEDBACK</h2>

      {!ratingLoading && schoolName && (
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-1">Driving School Feedback</h5>
              <p className="text-muted mb-0">
                Share your rating and experience for your assigned driving school. This feedback will be used in future school matching.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-light text-dark border">
                {hasExistingSchoolRating ? 'Submitted review' : 'Driving school review'}
              </span>
              {hasExistingSchoolRating && !isEditingSchoolRating && (
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setIsEditingSchoolRating(true);
                    setRatingError(null);
                  }}
                >
                  Edit
                </Button>
              )}
            </div>
          </div>

          <>
            <div className="mb-3">
              <div className="fw-bold mb-1">Assigned Driving School</div>
              <div className="text-muted">{schoolName}</div>
            </div>

            <div>
            {ratingSuccess && (
              <Alert variant="success" className="mb-3">
                Driving school rating submitted successfully.
              </Alert>
            )}

            {ratingError && (
              <Alert variant="danger" className="mb-3">
                {ratingError}
              </Alert>
            )}

            {hasExistingSchoolRating && !isEditingSchoolRating ? (
              <div>
                <div className="mb-3">
                  <div className="fw-bold mb-1">Your Rating</div>
                  <div className="text-muted">
                    {schoolRating} Star{schoolRating > 1 ? 's' : ''}
                  </div>
                </div>
                <div>
                  <div className="fw-bold mb-1">Your Review</div>
                  <div className="text-muted">
                    {schoolReview?.trim() || 'No written review added.'}
                  </div>
                </div>
              </div>
            ) : (
            <Form onSubmit={handleSchoolRatingSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold d-block">School Rating</Form.Label>
                <div className="d-flex gap-2 flex-wrap">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Button
                      key={value}
                      type="button"
                      variant={value <= schoolRating ? 'warning' : 'outline-secondary'}
                      onClick={() => {
                        setSchoolRating(value);
                        setRatingError(null);
                      }}
                      disabled={ratingSaving}
                    >
                      {value} Star{value > 1 ? 's' : ''}
                    </Button>
                  ))}
                </div>
                <Form.Text className="text-muted">
                  Select an overall rating for your driving school experience.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">School Review</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={schoolReview}
                  onChange={(e) => setSchoolReview(e.target.value)}
                  placeholder="Share your overall experience with the driving school, instructors, support, and scheduling."
                  disabled={ratingSaving}
                  style={{ resize: 'vertical' }}
                />
              </Form.Group>

              <Button type="submit" variant="primary" disabled={ratingSaving || !schoolRating}>
                {ratingSaving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving Rating...
                  </>
                ) : (
                  hasExistingSchoolRating ? 'Update Driving School Feedback' : 'Submit Driving School Feedback'
                )}
              </Button>
            </Form>
            )}
            </div>
            </>
        </Card.Body>
      </Card>
      )}

      {success && (
        <Alert variant="success" className="mb-3">
          Thank you! Your feedback has been submitted successfully.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <h5 className="mb-1">Overall Feedback</h5>
                <p className="text-muted mb-0">
                  Share your overall feedback about ASYSTA, the platform experience, support, and suggestions for improvement.
                </p>
              </div>
              {hasExistingOverallFeedback && !isEditingOverallFeedback && (
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setIsEditingOverallFeedback(true);
                    setError(null);
                  }}
                >
                  Edit
                </Button>
              )}
            </div>
          </div>
          {overallLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              Loading overall feedback...
            </div>
          ) : hasExistingOverallFeedback && !isEditingOverallFeedback ? (
            <div>
              <div className="fw-bold mb-1">Your Overall Feedback</div>
              <div className="text-muted">{feedback}</div>
            </div>
          ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Overall Feedback</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={feedback}
                onChange={handleFeedbackChange}
                placeholder="Write your overall feedback here... Tell us what you like, what can be improved, or any suggestions you have."
                disabled={loading}
                style={{ resize: 'vertical' }}
              />
              <Form.Text className="text-muted">
                We appreciate your honest overall feedback to help us improve our services.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="primary"
                type="submit"
                disabled={loading || !feedback.trim()}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  hasExistingOverallFeedback ? 'Update Overall Feedback' : 'Submit Overall Feedback'
                )}
              </Button>

              {feedback.trim() && (
                <span className="text-muted">
                  {feedback.length} character{feedback.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </Form>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Feedback;
